import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { supabase } from "../../lib/supabase";

/**
 * "Was this helpful?" answers from HoW's help center (ADR 0059 amendment, D27), for the control plane's Help feedback
 * page. platform_help_feedback returns every article answered in the period with its Yes and No counts, and the newest
 * comments; DCX Logic staff only (super admin and support), which the database enforces whatever this screen shows.
 */

export const PERIODS = {
  "30": "Last 30 days",
  "90": "Last 90 days",
  "365": "Last year",
} as const;
export type Period = keyof typeof PERIODS;

/** The period in the address, or 90 days when it is missing or not one of the choices. */
export function period(value: string): Period {
  return Object.hasOwn(PERIODS, value) ? (value as Period) : "90";
}

const articleRow = z.object({
  article: z.string(),
  helpful: z.number(),
  notHelpful: z.number(),
  comments: z.number(),
  lastAt: z.string(),
});
export type ArticleFeedback = z.infer<typeof articleRow>;

const commentRow = z.object({
  id: z.string(),
  article: z.string(),
  comment: z.string(),
  createdAt: z.string(),
});
export type FeedbackComment = z.infer<typeof commentRow>;

const summary = z.object({
  since: z.string(),
  articles: z.array(articleRow),
  comments: z.array(commentRow),
});
export type HelpFeedbackSummary = z.infer<typeof summary>;

export function parseSummary(data: unknown): HelpFeedbackSummary {
  return summary.parse(data);
}

/** The share of answers that said Yes, 0 to 100, or null with no answers. */
export function helpfulShare(row: Pick<ArticleFeedback, "helpful" | "notHelpful">): number | null {
  const total = row.helpful + row.notHelpful;
  return total === 0 ? null : Math.round((row.helpful / total) * 100);
}

/** An article's file name in words, until the page knows its title: "connecting-stripe" → "Connecting stripe". */
export function articleName(slug: string): string {
  const words = slug.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The article in the help center; /help/articles/<file> always finds it, wherever its category is. */
export function articleUrl(baseDomain: string, slug: string): string {
  return `https://${baseDomain}/help/articles/${slug}`;
}

/** Least helpful first: the most No answers, then the lowest share of Yes, then the name. */
export function byLeastHelpful(rows: readonly ArticleFeedback[]): ArticleFeedback[] {
  return [...rows].sort(
    (a, b) =>
      b.notHelpful - a.notHelpful ||
      (helpfulShare(a) ?? 100) - (helpfulShare(b) ?? 100) ||
      a.article.localeCompare(b.article),
  );
}

export function useHelpFeedback(days: Period) {
  return useQuery({
    queryKey: ["help-feedback", days],
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<HelpFeedbackSummary> => {
      const { data, error } = await supabase.rpc("platform_help_feedback", {
        p_days: Number(days),
      });
      if (error) throw error;
      return parseSummary(data);
    },
  });
}
